import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Quantity, RestDataService, SolverApiService, UserService } from 'pumpcalc-core'
import { Gas, Pipe } from 'projects/pumpcalc-core/src/lib/datatypes.service';
import { Keys, Labels } from './gasflow.constants';
import { environment } from '../environments/environment';
import { catchError, mergeMap } from 'rxjs/operators';
import { RangeVariable } from './range-variable-form/range-variable-form.component';

export class SolverResultConditionsRangeVariable {
  solverResult:any;
  conditions:any;
  rangeVariable?:any
}

@Injectable({
  providedIn: 'root'
})
export class GasflowSolverService {

  constructor(private restDataService:RestDataService, private solverApiService:SolverApiService, private userService:UserService) { }

  readonly SOLVERSERVICEERROR = "Solver Service Error";

  createCalculationParameters (
    pipes:Pipe[],
    gas:Gas,
    gasTemperature:number,
    gasflowBoundaryConditions:GasflowBoundaryConditions, rangeVariables:any):CalculationParameters {
      return {
      "Parameters":{
      "GasId": gas.id,
      "GasEntryTemperature" :gasTemperature,
      "BoundaryConditions" : gasflowBoundaryConditions,
      "PipeWork": this.createPipeWork(pipes)
      },
      "Range": this.isNotEmpty(rangeVariables) ? this.createRange(rangeVariables) : null,
      "UserId": this.userService.getUserId()
    }

  }

  createPipeWork(pipes:Pipe[]):PipeDefinition[]{
    let pipeWork:PipeDefinition[]=[];
    pipes.forEach(function(pipe:Pipe){
      let pipeDefinition:PipeDefinition=new PipeDefinition();
      pipeDefinition.Bends = pipe.bends;
      pipeDefinition.IsEntrance = pipe.entrance;
      pipeDefinition.IsFlexible = pipe.flexible;
      pipeDefinition.Length = pipe.length;
      pipeDefinition.ShapeSection = ShapeSection[pipe.section];
      pipeDefinition.XDim1 = pipe.xdim1;
      pipeDefinition.XDim2 = pipe.xdim2;
      pipeDefinition.Parallel = pipe.parallel;
      pipeWork.push(pipeDefinition);
    })
    return pipeWork;
  }


  createRange(rangeVariable){
    return {
      "Parameter": this.parameterValue(rangeVariable[1].key,rangeVariable[1].quantity),
      "MinimumValue" : rangeVariable[1].min,
      "MaximumValue" : rangeVariable[1].max,
      "NumberOfIntervals": rangeVariable[1].intervals,
      "IsLogarithmicSpacing": rangeVariable[1].log,
      "PipeNumber": this.isDefined(rangeVariable[1].pipe)?rangeVariable[1].pipe-1:null
   }
  }

  calculate(calculationParameters:CalculationParameters):Observable<any>{
    return this.restDataService.post(environment.jobsUrl, calculationParameters).pipe(mergeMap(
      job =>{
        return this.solverApiService.pollJobUntilReady(environment.jobsUrl + "/" + job.id).pipe(
          mergeMap(completedJob =>{
            return this.restDataService.get(environment.jobsUrl + "/" + completedJob.id +"/results").pipe(catchError(this.handleError()))
          })
        ).pipe(catchError(this.handleError()))
      }
    )).pipe(catchError(this.handleError()))
  }

  private handleError<T> (operation = 'operation') {
    return (error: any): Observable<T> => {
      console.error(error);
      throw new Error(this.SOLVERSERVICEERROR);
    };
  }

  stopCalculating(){
    this.solverApiService.stopPolling();
  }

  isDefined(variable):boolean {
    return typeof(variable) != "undefined"
  }

  isNotEmpty(obj){
    return Object.keys(obj).length > 0;
  }

  parameterValue(key,quantity){
    if(key == Keys.systemThroughput){
      switch(quantity){
        case Quantity.THROUGHPUT : return "BoundaryConditionThroughput";
        case Quantity.THROUGHPUT_MASS_FLOW : return "BoundaryConditionThroughputMassFlow";
        case Quantity.THROUGHPUT_MOLAR_FLOW :return "BoundaryConditionThroughputMolarFlow";
      }
    }
    return ParameterValues[key];
  }

}

export class CalculationParameters {
  Parameters : {
    calculationId?:string;
    GasId:string;
    GasEntryTemperature:number;
    BoundaryConditions:GasflowBoundaryConditions;
    PipeWork:PipeDefinition[];
  }
  UserId:string;
  id?:string;
  Range:Range
}

export class GasflowBoundaryConditions {
  Type:GasFlowBoundaryConditionsType;
  BoundaryCondition1:number;
  BoundaryCondition2:number;
}

export class PipeDefinition {
  ShapeSection:string;
  Length:number;
  XDim1:number;
  XDim2:number;
  Parallel:number;
  Bends:number;
  IsFlexible:boolean;
  IsEntrance:boolean;
}

export enum GasFlowBoundaryConditionsType {
  PuPd	= "PuPd",
  PuQ	  = "PuQ",
  PuS	  = "PuS",
  PdQ	  = "PdQ",
  PdS   = "PdS",
  PuQmol = "PuQmol",
  PdQmol = "PdQmol",
  PuQmass = "PuQmass",
  PdQmass =  "PdQmass",
}


export const ShapeSection = {
  [Labels[Keys.circular]]:"circular",
  [Labels[Keys.annular]]:"annular",
  [Labels[Keys.rectangular]]:"rectangular",
}

export class Range {
  Parameter:string;
  MinimumValue:number;
  MaximumValue:number;
  NumberOfIntervals:number;
  IsLogarithmicSpacing:boolean;
  PipeNumber:number;
}

const ParameterValues = {
  "systemThroughput":"BoundaryConditionThroughput",
  "systemDownstreamPressure" : "BoundaryConditionDownStreamPressure",
  "systemDownstreamSpeed" : "BoundaryConditionDownStreamSpeed",
  "systemUpstreamPressure" : "BoundaryConditionUpStreamPressure",
  "gasEntryTemperature" : "GasTemperature",
  "diameter" : "PipeXDim1",
  "height" : "PipeXDim1",
  "width" : "PipeXDim2",
  "insideDiameter" : "PipeXDim1",
  "radialGap" : "PipeXDim2",
  "parallel" : "PipeNumberInParallel",
  "length" : "PipeLength",
  "bends" : "PipeBends",
}



